import { createSelector } from '@reduxjs/toolkit'

import { IApplicationState } from '@store'
import { selectCampaign, selectCampaignLink } from '@store/campaigns'
import {
  ATTACH_NOTES_REL,
  ATTACH_TRANSCRIPT_REL,
  DETACH_NOTES_REL,
  DETACH_TRANSCRIPT_REL,
  START_DISCOVERY_RUN_REL,
} from './api'
import { ISourcingBlock } from './types'

const selectDiscoveryRunsSlice = (state: IApplicationState) => state.discoveryRuns

// The sourcing block lives on the campaign itself (arrives for free on the
// existing brief-view fetch) — NOT a separate discoveryRuns-owned fetch.
// Absent entirely when sourcing isn't enabled/legal for this brief, or
// before the campaign has loaded at all.
export const selectSourcingBlock = (
  state: IApplicationState,
  campaignId: number,
): ISourcingBlock | undefined => selectCampaign(state, campaignId)?.sourcing

export const selectAttachingTranscript = createSelector(
  selectDiscoveryRunsSlice,
  (s) => s.attachingTranscript,
)

export const selectAttachingNotes = createSelector(
  selectDiscoveryRunsSlice,
  (s) => s.attachingNotes,
)

export const selectStartingDiscoveryRun = createSelector(
  selectDiscoveryRunsSlice,
  (s) => s.starting,
)

// BRAND-9582 item 13 (FINAL fix): mirrors selectAttachingTranscript/Notes for
// the real detach mutations.
export const selectDetachingTranscript = createSelector(
  selectDiscoveryRunsSlice,
  (s) => s.detachingTranscript,
)

export const selectDetachingNotes = createSelector(
  selectDiscoveryRunsSlice,
  (s) => s.detachingNotes,
)

// Gate for rendering the first-run setup form: the sourcing block has
// loaded, there are zero existing sourcing runs, and the campaign carries
// either the attach_transcript or start_discovery_run rel.
//
// attach_transcript is the TRUE zero-state signal — the BE only issues it
// once staff + the sourcing toggle + creator_sourcing_enabled + the
// attachable brief state all line up, and it's present from the very first
// brief-view fetch (before anything is attached). start_discovery_run is
// only issued LATER, once a transcript has actually been attached — so
// gating on it alone made the form's own precondition unreachable (a fresh,
// nothing-attached brief would never show the form that attaches the
// transcript in the first place). Keeping start_discovery_run as a fallback
// disjunct keeps the form visible in the attached-but-never-started state
// too (e.g. after a refresh where attach_transcript may no longer be
// offered but the run hasn't started yet).
export const selectIsFirstRunSetupState = (
  state: IApplicationState,
  campaignId: number,
): boolean => {
  const block = selectSourcingBlock(state, campaignId)
  if (!block) return false
  return (
    block.sourcing_runs.length === 0 &&
    (Boolean(selectCampaignLink(state, campaignId, ATTACH_TRANSCRIPT_REL)) ||
      Boolean(selectCampaignLink(state, campaignId, START_DISCOVERY_RUN_REL)))
  )
}

export const selectCanAttachTranscript = (state: IApplicationState, campaignId: number): boolean =>
  Boolean(selectCampaignLink(state, campaignId, ATTACH_TRANSCRIPT_REL))

export const selectCanAttachNotes = (state: IApplicationState, campaignId: number): boolean =>
  Boolean(selectCampaignLink(state, campaignId, ATTACH_NOTES_REL))

// BRAND-9582 item 13: the detach rel is the single source of truth for
// rendering Remove — mirrors selectCanAttachTranscript/Notes above (see
// DETACH_TRANSCRIPT_REL's doc in api.ts for when the BE issues it).
export const selectCanDetachTranscript = (state: IApplicationState, campaignId: number): boolean =>
  Boolean(selectCampaignLink(state, campaignId, DETACH_TRANSCRIPT_REL))

export const selectCanDetachNotes = (state: IApplicationState, campaignId: number): boolean =>
  Boolean(selectCampaignLink(state, campaignId, DETACH_NOTES_REL))

export const selectCanStartDiscoveryRun = (state: IApplicationState, campaignId: number): boolean =>
  Boolean(selectCampaignLink(state, campaignId, START_DISCOVERY_RUN_REL))

// --- BRAND-9511 run limit ---

// runs_used and max_sourcing_runs are read directly off the sourcing block at
// the call sites (which already hold it), same as transcript_attached et al —
// only the at-limit derivation below earns a named selector.
//
// At the limit the BE also stops issuing start_discovery_run (so
// selectCanStartDiscoveryRun goes false anyway) — this selector exists to
// make the WHY legible: "+ New run" disabled-with-reason and the exhausted
// runs-used header need "at the cap" as a distinct signal from "link absent
// for some other reason" (e.g. a run in progress).
export const selectAtRunLimit = (state: IApplicationState, campaignId: number): boolean => {
  const block = selectSourcingBlock(state, campaignId)
  if (!block || typeof block.max_sourcing_runs !== 'number') return false
  return block.runs_used >= block.max_sourcing_runs
}
